import { getAuth, signInWithEmailAndPassword } from '@firebase/auth';
import { Button, TextField } from '@mui/material';
import React, { FC } from 'react';
import { useForm } from 'react-hook-form';
import styles from './Login.module.css';
import { Link, useNavigate } from 'react-router';
import { handleError } from '../../../common/firebase/auth-error.service';
import { severity } from '../../../common/models/severity.enum';
import { ServiceManager } from '../../../services/service-manager';
import { emailRegex } from '../../../common/firebase/regex';
import { firestoreCollections } from '../../../services/firestore-collections.enum';
import { FirestoreService } from '../../../common/firebase/firestore.service';
import { UserData } from '../models/user';


export interface LoginProps {
    user: UserData;
    setUser: (user: UserData) => void;
    termsAndConditionsLink: string;
    privacyStatementLink: string;
}

const Login: FC<LoginProps> = ({ user, setUser, termsAndConditionsLink, privacyStatementLink }) => {

    const navigate = useNavigate();

    const { register, handleSubmit, formState: { errors } } = useForm<{ email: string, password: string }>();

    const onSubmit = (data) => {
        const auth = getAuth();
        signInWithEmailAndPassword(auth, data.email, data.password)
            .then(async () => {
                ServiceManager.snackbarService.openSnackbar('U bent ingelogd.', severity.success, 3000);
                const userPrivateData = await FirestoreService.getDoc(`${firestoreCollections.users}/${data.email}/data`, 'private',
                    (error) => {
                        ServiceManager.snackbarService.openSnackbar(`Nieuwe gebruiker heeft ingelogd.`, severity.success);
                        navigate('/auth/new-user');
                    });

                const userPublicData = await FirestoreService.getDoc(`${firestoreCollections.users}/${data.email}/data`, 'public',
                    (error) => { });
                const token = await auth.currentUser.getIdTokenResult();

                if (userPrivateData && userPublicData) {
                    setUser({ ...auth.currentUser, private: userPrivateData, public: userPublicData, token } as any);
                    setTimeout(() => {
                        navigate('/app');
                    })
                }
            })
            .catch((error) => {
                handleError(error);
            });
    }

    return (
        <div className={styles.Login} data-testid="Login">
            <form data-testid="login-form" onSubmit={handleSubmit(onSubmit)}>
                <div>
                    <TextField id="login-email"
                        fullWidth
                        className="form-input"
                        label="E-mailadres"
                        {...register('email', {
                            required: { value: true, message: 'U moet uw e-mailadres invullen.' }, pattern: {
                                value: emailRegex,
                                message: 'Het e-mailadres voldoet niet aan de regels.'
                            }
                        })}
                        variant="standard"
                    />
                    {errors.email && <p className="input-error">{errors.email.message}</p>}
                </div>
                <div>
                    <TextField id="login-password"
                        fullWidth
                        className="form-input"
                        label="Wachtwoord"
                        {...register('password', { required: { value: true, message: 'U moet uw wachtwoord invullen.' }, minLength: { value: 6, message: 'Het wachtoord moet uit minimaal 6 karakters bestaan.' } })}
                        variant="standard"
                        type="password"
                    />
                    {errors.password && <p className="input-error">{errors.password.message}</p>}
                </div>
                <div className={styles.actions}>
                    <div>
                        <Button color="primary" data-testid="login-submit" variant="contained" type="submit">Login</Button>
                    </div>
                    <div>
                        <Button color="secondary" data-testid="login-new-account" type="button" variant="outlined" onClick={() => {
                            navigate('/auth/signup')
                        }}>Nieuw Account</Button>
                    </div>
                    <p>
                        <Link className="page-scroll" to="/auth/forgot-password">Wachtwoord vergeten</Link>
                    </p>
                </div>
            </form>
            <p className={styles.notice}>
                <em>
                    Door in te loggen ga je akkoord met onze <a target="_blank" rel="noreferrer" href={termsAndConditionsLink}>Gebruiksvoorwaarden</a> en <a target="_blank" rel="noreferrer" href={privacyStatementLink}>Privacy</a> statement.
                </em>
            </p>
        </div>
    )
};

export default Login;

